import { DatePicker, TimePicker } from '@mui/lab';
import { Divider, Grid, SelectChangeEvent, useEventCallback } from '@mui/material';
import { AccordionGeneralityForm } from 'civil-status/view-models/accordion-generality-form';
import ListGender from 'shell/components/shared/gender';
import { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { FormikErrors, FormikTouched } from 'formik';
import { ItemVM } from 'shell/view-models/item';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import ListVerificationType from '../list/list-verification-type';
import ListIstat from '../list/list-istat';
import ListBirthPlace, { ID_BIRTH_PLACE, ItemBirthPlaceVM } from '../list/list-birth-place';
import ListResidenceBorn, { ID_RESIDENCE_LIST } from '../list/list-residence-born';
import Italian from '../../../shared/italian';
import { ItalianForm } from 'shell/view-models/italian-form';
import BornNascita03 from './born-nascita-03';
import { DateTime } from 'luxon';
import Twins from '../shared/twins';
import { CodeSubject } from 'civil-status/constants/code-subject-section';
import ListReasonChoosingSurname, {
  ID_CHOOSING_SURNAME,
} from '../list/list-reason-choosing-surname';
import ListAnsc from '../../../../../../../shared/list-ansc';
import { ID_SURNAME_TYPE } from 'civil-status/constants/id-surname-type';
import { IdResidence } from 'civil-status/constants/id-residence';

interface FormGeneralityBornProps {
  disabled?: boolean;
  generality?: AccordionGeneralityForm;
  errorsGenerality?: FormikErrors<AccordionGeneralityForm>;
  touchedGenerality?: FormikTouched<AccordionGeneralityForm>;
  onChangeGenerality: (
    key: keyof AccordionGeneralityForm,
    value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
  ) => void;
  onChangeFieldsGenerality: (values: AccordionGeneralityForm) => void;
  configuration?: string;
  codeSubject?: string;
  surnameMother?: string;
  surnameFather?: string;
  valuesMother?: AccordionGeneralityForm;
  valuesFather?: AccordionGeneralityForm;
  dateFormationAct?: string;
  withReasonDelay?: boolean;
  isSaved?: boolean;
  isLastStepEmergency?: boolean;
  tipoEvento?: string;
}

const FormGeneralityBornComponent = ({
  disabled = false,
  generality,
  errorsGenerality,
  touchedGenerality,
  onChangeGenerality,
  onChangeFieldsGenerality,
  surnameMother,
  surnameFather,
  withReasonDelay,
  valuesMother,
  valuesFather,
  dateFormationAct,
  configuration,
  codeSubject,
  isLastStepEmergency,
  tipoEvento,
}: FormGeneralityBornProps): React.ReactElement => {
  // errore madre per cognome
  const [errorMother, setErrorMother] = useState<boolean>(false);
  // errore padre per cognome
  const [errorFather, setErrorFather] = useState<boolean>(false);
  // errore per residenza
  const [errorResidence, setErrorResidence] = useState<{
    errorMother?: boolean;
    errorFather?: boolean;
  }>({ errorFather: false, errorMother: false });

  const isNascita02: boolean = useMemo(() => {
    return configuration === ConfigurationType.NASC02;
  }, [configuration]);

  const isNascita03: boolean = useMemo(() => {
    return configuration === ConfigurationType.NASC03;
  }, [configuration]);

  const isNascita04: boolean = useMemo(() => {
    return configuration === ConfigurationType.NASC04;
  }, [configuration]);

  const isNascita05: boolean = useMemo(() => {
    return configuration === ConfigurationType.NASC05;
  }, [configuration]);

  const isNascita06: boolean = useMemo(() => {
    return configuration === ConfigurationType.NASC06;
  }, [configuration]);

  const isNascita07: boolean = useMemo(() => {
    return configuration === ConfigurationType.NASC07;
  }, [configuration]);

  const isNascita99: boolean = useMemo(() => {
    return configuration === ConfigurationType.NASC99;
  }, [configuration]);

  const handleListGenderChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChangeGenerality('gender', event.target.value);
  });

  const handleListSurnameChange = useEventCallback((item?: ItemVM) => {
    controlSurname(item);
  });

  const controlSurname = useEventCallback((surnameItem?: ItemVM) => {
    switch (surnameItem?.id) {
      case ID_SURNAME_TYPE.Madre:
        if (!surnameMother) {
          setErrorFather((oldState) => (oldState ? false : oldState));
          setErrorMother(true);

          onChangeFieldsGenerality({
            ...(generality || {}),
            surnameItem,
            surname: '',
          });
        } else {
          // se presente annullare l'errore
          setErrorMother((oldState) => (oldState ? false : oldState));
          setErrorFather((oldState) => (oldState ? false : oldState));

          onChangeFieldsGenerality({
            ...(generality || {}),
            surnameItem,
            surname: surnameMother,
          });
        }
        break;
      case ID_SURNAME_TYPE.Padre:
        if (!surnameFather) {
          setErrorMother((oldState) => (oldState ? false : oldState));
          setErrorFather(true);

          onChangeFieldsGenerality({
            ...(generality || {}),
            surnameItem,
            surname: '',
          });
        } else {
          // se presente annullare l'errore
          setErrorMother((oldState) => (oldState ? false : oldState));
          setErrorFather((oldState) => (oldState ? false : oldState));

          onChangeFieldsGenerality({
            ...(generality || {}),
            surnameItem,
            surname: surnameFather,
          });
        }
        break;
      case ID_SURNAME_TYPE.PadreMadre:
        let errorPadreMadre = false;

        // controllo padre
        if (!surnameFather) {
          errorPadreMadre = true;
          setErrorFather(true);
        } else {
          setErrorFather((oldState) => (oldState ? false : oldState));
        }

        // controllo madre
        if (!surnameMother) {
          errorPadreMadre = true;
          setErrorMother(true);
        } else {
          setErrorMother((oldState) => (oldState ? false : oldState));
        }

        if (errorPadreMadre) {
          onChangeFieldsGenerality({
            ...(generality || {}),
            surnameItem,
            surname: '',
          });
        } else {
          onChangeFieldsGenerality({
            ...(generality || {}),
            surnameItem,
            surname: `${surnameFather} ${surnameMother}`,
          });
        }

        break;
      case ID_SURNAME_TYPE.MadrePadre:
        let errorMadrePadre = false;

        // controllo padre
        if (!surnameFather) {
          errorMadrePadre = true;
          setErrorFather(true);
        } else {
          setErrorFather((oldState) => (oldState ? false : oldState));
        }

        // controllo madre
        if (!surnameMother) {
          errorMadrePadre = true;
          setErrorMother(true);
        } else {
          setErrorMother((oldState) => (oldState ? false : oldState));
        }

        if (errorMadrePadre) {
          onChangeFieldsGenerality({
            ...(generality || {}),
            surnameItem,
            surname: '',
          });
        } else {
          onChangeFieldsGenerality({
            ...(generality || {}),
            surnameItem,
            surname: `${surnameMother} ${surnameFather}`,
          });
        }

        break;
      default:
        setErrorMother((oldState) => (oldState ? false : oldState));
        setErrorFather((oldState) => (oldState ? false : oldState));

        onChangeFieldsGenerality({
          ...(generality || {}),
          surnameItem,
          surname: '',
        });
        break;
    }
  });

  const handleListVerificationTypeChange = useEventCallback((item?: ItemVM) => {
    onChangeGenerality('verificationTypeItem', item);
  });

  const disabledSurname = useMemo(() => {
    if (disabled) {
      return true;
    }

    if (isNascita05 || isNascita04) {
      return false;
    }

    return !(generality?.surnameItem?.id === ID_SURNAME_TYPE.Altro);
  }, [disabled, generality?.surnameItem, isNascita05, isNascita04]);

  const handleListBirthPlaceItemChange = useEventCallback((item?: ItemBirthPlaceVM) => {
    onChangeFieldsGenerality({
      ...(generality || {}),
      birthPlaceItem: item,
      italian:
        item?.id !== ID_BIRTH_PLACE.AltroComune ? generality?.italian : { municipality: undefined },
      descriptionBirthPlace: item?.descrizioneValore || '',
      codeIstat: item?.codiceStatistico
        ? {
            id: item?.codiceStatistico,
          }
        : undefined,
    } as AccordionGeneralityForm);
  });

  const handleListIstatItemChange = useEventCallback((item?: ItemVM) => {
    onChangeGenerality('codeIstat', item);
  });

  const handleListResidenceBornChange = useEventCallback((item?: ItemVM) => {
    if (item?.id === ID_RESIDENCE_LIST.Nessuno) {
      onChangeFieldsGenerality({
        ...(generality || {}),
        residenceId: item.id,
        italianResidence: undefined,
        address: '',
      });
    }

    if (item?.id === ID_RESIDENCE_LIST.ConMadre) {
      onChangeFieldsGenerality({
        ...(generality || {}),
        residenceId: item.id,
        typeResidence: valuesMother?.typeResidence,
        italianResidence: valuesMother?.italianResidence,
        address: valuesMother?.address || '',
      });

      // controllo errori
      if (valuesMother?.italianResidence?.municipality?.description) {
        setErrorResidence({
          errorFather: false,
          errorMother: false,
        });
      } else {
        setErrorResidence({
          errorFather: false,
          errorMother: true,
        });
      }
    }

    if (item?.id === ID_RESIDENCE_LIST.ConPadre) {
      onChangeFieldsGenerality({
        ...(generality || {}),
        residenceId: item.id,
        typeResidence: valuesFather?.typeResidence,
        italianResidence: valuesFather?.italianResidence,
        address: valuesFather?.address || '',
      });

      // controllo errori
      if (valuesFather?.italianResidence?.municipality?.description) {
        setErrorResidence({
          errorFather: false,
          errorMother: false,
        });
      } else {
        setErrorResidence({
          errorFather: true,
          errorMother: false,
        });
      }
    }

    if (item?.id === ID_RESIDENCE_LIST.AltroIndirizzo) {
      onChangeFieldsGenerality({
        ...(generality || {}),
        residenceId: item.id,
        italianResidence: undefined,
        address: undefined,
      });

      setErrorResidence({
        errorFather: false,
        errorMother: false,
      });
    }
  });

  const onFieldItalianResidenceChange = useCallback(
    (key, value) => {
      onChangeGenerality('italianResidence', {
        ...(generality || {}).italianResidence,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldsItalianResidenceChange = useCallback(
    (valuesNew) => {
      onChangeGenerality('italianResidence', {
        ...(generality || {}).italianResidence,
        ...valuesNew,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChangeGenerality('italian', {
        ...(generality || {}).italian,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldsItalianChange = useCallback(
    (valuesNew) => {
      onChangeGenerality('italian', {
        ...(generality || {}).italian,
        ...valuesNew,
      });
    },
    [generality, onChangeGenerality]
  );

  useEffect(() => {
    // resettare se cambia la residenza del genitore selezionato

    if (
      generality?.residenceId === ID_RESIDENCE_LIST.ConMadre &&
      valuesMother?.typeResidence === IdResidence.Foreign
    ) {
      onChangeFieldsGenerality({
        ...(generality || {}),
        residenceId: undefined,
        italianResidence: undefined,
      });

      setErrorResidence({
        errorFather: false,
        errorMother: false,
      });

      return;
    }

    if (
      generality?.residenceId === ID_RESIDENCE_LIST.ConPadre &&
      valuesFather?.typeResidence === IdResidence.Foreign
    ) {
      onChangeFieldsGenerality({
        ...(generality || {}),
        residenceId: undefined,
        italianResidence: undefined,
      });

      setErrorResidence({
        errorFather: false,
        errorMother: false,
      });

      return;
    }
  }, [valuesFather, valuesMother]);

  const handleListTypologyBorn = useEventCallback((item?: ItemVM) => {
    onChangeGenerality('bornTypology', item?.id);
  });

  const handleListReasonChoosingSurname = useEventCallback((item?: ItemVM) => {
    onChangeFieldsGenerality({
      ...(generality || {}),
      clarificationSurname: '',
      reasonChoosingSurname: item,
    });
  });

  return (
    <Grid container spacing={2} item>
      {codeSubject === CodeSubject.INT_NAS ? (
        <Twins
          generality={generality}
          onChangeFieldsGenerality={onChangeFieldsGenerality}
          onChangeGenerality={onChangeGenerality}
          disabled={disabled}
          errorsGenerality={errorsGenerality}
          touchedGenerality={touchedGenerality}
        />
      ) : undefined}
      {isNascita05 || isNascita04 ? undefined : (
        <Grid item xs={12} md={3}>
          <ListAnsc
            label="Scelta cognome"
            idDominio="31"
            required
            value={generality?.surnameItem?.id?.toString() || ''}
            onChange={handleListSurnameChange}
            disabled={disabled}
            error={Boolean(errorsGenerality?.surname) && touchedGenerality?.surname}
            tipoEvento={isLastStepEmergency ? tipoEvento : undefined}
          />
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Cognome *"
          aria-label="Cognome"
          id="surname"
          fullWidth
          disabled={disabledSurname}
          inputProps={{
            maxLength: '80',
          }}
          helperText={errorsGenerality?.surname}
          error={touchedGenerality?.surname && Boolean(errorsGenerality?.surname)}
          value={generality?.surname || ''}
          onChange={(event) => {
            onChangeGenerality('surname', event.target.value);
          }}
        />
      </Grid>
      {errorFather || errorMother ? (
        <Grid
          item
          xs={12}
          md={3}
          container
          spacing={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {errorFather ? (
            <div style={{ color: 'red' }}>Inserire il cognome del Padre.</div>
          ) : undefined}
          {errorMother ? (
            <div style={{ color: 'red', paddingTop: errorFather ? '16px' : '' }}>
              Inserire il cognome della Madre.
            </div>
          ) : undefined}
        </Grid>
      ) : undefined}
      <Grid item xs={12} md={9}>
        <PmiTextField
          label="Nome *"
          aria-label="Nome"
          id="name"
          fullWidth
          disabled={disabled}
          inputProps={{
            maxLength: '80',
          }}
          helperText={errorsGenerality?.name}
          error={touchedGenerality?.name && Boolean(errorsGenerality?.name)}
          value={generality?.name || ''}
          onChange={(event) => {
            onChangeGenerality('name', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ListGender
          required
          value={generality?.gender || ''}
          onChange={handleListGenderChange}
          disabled={disabled}
          error={Boolean(errorsGenerality?.gender) && touchedGenerality?.gender}
          helperText={errorsGenerality?.gender}
          withoutUnknown
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <ListReasonChoosingSurname
          value={generality?.reasonChoosingSurname?.id?.toString() || ''}
          onChange={handleListReasonChoosingSurname}
          disabled={disabled}
          error={
            Boolean(errorsGenerality?.reasonChoosingSurname) &&
            touchedGenerality?.reasonChoosingSurname
          }
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        {generality?.reasonChoosingSurname?.id?.toString() === ID_CHOOSING_SURNAME.Altro ? (
          <PmiTextField
            label="Motivo scelta cognome *"
            aria-label="Motivo scelta cognome"
            id="clarificationSurname"
            fullWidth
            disabled={disabled}
            helperText={errorsGenerality?.clarificationSurname}
            error={
              touchedGenerality?.clarificationSurname &&
              Boolean(errorsGenerality?.clarificationSurname)
            }
            value={generality?.clarificationSurname || ''}
            onChange={(event) => {
              onChangeGenerality('clarificationSurname', event.target.value);
            }}
          />
        ) : undefined}
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} md={3}>
        <DatePicker
          label="Data nascita *"
          aria-label="Data nascita"
          value={generality?.birthDate || null}
          onChange={(date) => {
            onChangeGenerality('birthDate', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="birthDate"
              fullWidth
              helperText={errorsGenerality?.birthDate}
              error={touchedGenerality?.birthDate && Boolean(errorsGenerality?.birthDate)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TimePicker
          label={`Ora nascita ${isNascita03 ? '' : '*'}`}
          aria-label="Ora nascita"
          value={generality?.birthTime || null}
          onChange={(date) => {
            onChangeGenerality('birthTime', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              id="birthTime"
              {...params}
              fullWidth
              error={touchedGenerality?.birthTime && Boolean(errorsGenerality?.birthTime)}
              helperText={errorsGenerality?.birthTime}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Codice fiscale"
          aria-label="Codice fiscale"
          name="fiscalCode"
          id="fiscalCode"
          fullWidth
          value={generality?.fiscalCode?.toUpperCase() || ''}
          onChange={(event) => onChangeGenerality('fiscalCode', event.target.value)}
          inputProps={{
            maxLength: '16',
          }}
          error={touchedGenerality?.fiscalCode && Boolean(errorsGenerality?.fiscalCode)}
          helperText={errorsGenerality?.fiscalCode}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ListBirthPlace
          required
          value={generality?.birthPlaceItem?.id?.toString() || ''}
          onChange={handleListBirthPlaceItemChange}
          disabled={disabled}
          error={Boolean(errorsGenerality?.birthPlaceItem) && touchedGenerality?.birthPlaceItem}
          dataReference={generality?.birthDate}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <PmiTextField
          label="Descrizione luogo nascita *"
          aria-label="Descrizione luogo nascita"
          id="descriptionBirthPlace"
          fullWidth
          disabled={
            disabled ||
            !(
              generality?.birthPlaceItem?.id?.toString() === ID_BIRTH_PLACE.AbitazionePrivata ||
              generality?.birthPlaceItem?.id?.toString() === ID_BIRTH_PLACE.AltroLuogo ||
              generality?.birthPlaceItem?.id?.toString() === ID_BIRTH_PLACE.AltroComune
            )
          }
          inputProps={{
            maxLength: '80',
          }}
          helperText={errorsGenerality?.descriptionBirthPlace}
          error={
            touchedGenerality?.descriptionBirthPlace &&
            Boolean(errorsGenerality?.descriptionBirthPlace)
          }
          value={generality?.descriptionBirthPlace || ''}
          onChange={(event) => {
            onChangeGenerality('descriptionBirthPlace', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <ListIstat
          required
          value={generality?.codeIstat?.id?.toString() || ''}
          onChange={handleListIstatItemChange}
          disabled={
            disabled ||
            !(
              generality?.birthPlaceItem?.id?.toString() === ID_BIRTH_PLACE.AltroLuogo ||
              generality?.birthPlaceItem?.id?.toString() === ID_BIRTH_PLACE.AltroComune
            )
          }
          error={Boolean(errorsGenerality?.codeIstat) && touchedGenerality?.codeIstat}
        />
      </Grid>
      {isNascita03 || isNascita07 ? (
        <BornNascita03
          disabled={disabled}
          generality={generality}
          errorsGenerality={errorsGenerality}
          touchedGenerality={touchedGenerality}
          onChangeGenerality={onChangeGenerality}
          onChangeFieldsGenerality={onChangeFieldsGenerality}
        />
      ) : (
        <Italian
          disabledComponent={
            disabled || generality?.birthPlaceItem?.id?.toString() !== ID_BIRTH_PLACE.AltroComune
          }
          values={generality?.italian}
          onChange={onFieldItalianChange}
          onChanges={onFieldsItalianChange}
          fromDate={generality?.birthDate || null}
          errors={errorsGenerality?.italian as FormikErrors<ItalianForm>}
          touched={touchedGenerality?.italian as unknown as FormikTouched<ItalianForm>}
          idDominio={generality?.birthPlaceItem?.idDominio}
          idValore={generality?.birthPlaceItem?.idValore}
          required
        />
      )}
      {isNascita04 || isNascita05 || isNascita07 || isNascita06 ? undefined : (
        <Fragment>
          <Grid item xs={12} md={6}>
            <ListResidenceBorn
              value={generality?.residenceId || ''}
              onChange={handleListResidenceBornChange}
              disabled={disabled}
              valuesFather={valuesFather}
              valuesMother={valuesMother}
              //error={Boolean(errorsGenerality?.residenceId) && touchedGenerality?.residenceId}
              error={errorResidence.errorFather || errorResidence.errorMother}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {errorResidence.errorFather ? (
              <span style={{ color: 'red' }}>Inserire la residenza del padre</span>
            ) : errorResidence.errorMother ? (
              <span style={{ color: 'red' }}>Inserire la residenza della madre</span>
            ) : undefined}
          </Grid>
          <Italian
            disabledComponent={
              disabled || generality?.residenceId !== ID_RESIDENCE_LIST.AltroIndirizzo
            }
            values={generality?.italianResidence}
            onChange={onFieldItalianResidenceChange}
            onChanges={onFieldsItalianResidenceChange}
            fromDate={dateFormationAct ? DateTime.fromISO(dateFormationAct) : null}
            errors={errorsGenerality?.italianResidence as FormikErrors<ItalianForm>}
            touched={touchedGenerality?.italianResidence as unknown as FormikTouched<ItalianForm>}
            labelMunicipality="Comune di residenza"
            labelProvince="Provincia di residenza"
          />
          <Grid item xs={12}>
            <PmiTextField
              aria-label="Indirizzo di residenza"
              label="Indirizzo di residenza"
              id="address"
              name="address"
              fullWidth
              disabled={disabled || generality?.residenceId !== ID_RESIDENCE_LIST.AltroIndirizzo}
              helperText={errorsGenerality?.address}
              error={touchedGenerality?.address && Boolean(errorsGenerality?.address)}
              type="text"
              variant="outlined"
              value={generality?.address || ''}
              onChange={(event) => {
                onChangeGenerality('address', event.target.value);
              }}
              inputProps={{
                maxLength: '240',
              }}
            />
          </Grid>
        </Fragment>
      )}
      {isNascita03 || isNascita07 || isNascita99 ? undefined : (
        <Grid item xs={12} md={6}>
          <ListVerificationType
            required
            value={generality?.verificationTypeItem?.id?.toString() || ''}
            onChange={handleListVerificationTypeChange}
            disabled={disabled}
            withoutFiglio={isNascita02}
            only1and2={isNascita04 || isNascita05}
            error={
              Boolean(errorsGenerality?.verificationTypeItem) &&
              touchedGenerality?.verificationTypeItem
            }
          />
        </Grid>
      )}
      {isNascita04 || isNascita02 ? (
        <Grid item xs={12} md={3}>
          <DatePicker
            label="Data medico necroscopo *"
            aria-label="Data medico necroscopo"
            value={generality?.necropsyDate || null}
            onChange={(date) => {
              onChangeGenerality('necropsyDate', date);
            }}
            disabled={disabled}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="necropsyDate"
                fullWidth
                helperText={errorsGenerality?.necropsyDate}
                error={touchedGenerality?.necropsyDate && Boolean(errorsGenerality?.necropsyDate)}
              />
            )}
          />
        </Grid>
      ) : undefined}
      {isNascita99 ? (
        <Grid item xs={12} md={4}>
          <ListAnsc
            label="Tipologia nascita"
            idDominio="107"
            required
            value={generality?.bornTypology || ''}
            onChange={handleListTypologyBorn}
            disabled={disabled}
            error={Boolean(errorsGenerality?.bornTypology) && touchedGenerality?.bornTypology}
          />
        </Grid>
      ) : undefined}
      {withReasonDelay ? (
        <Grid item xs={12} md={6}>
          <PmiTextField
            aria-label="Motivo ritardo comunicazione"
            label="Motivo ritardo comunicazione *"
            id="reasonDelay"
            name="reasonDelay"
            fullWidth
            disabled={disabled}
            helperText={errorsGenerality?.reasonDelay}
            error={touchedGenerality?.reasonDelay && Boolean(errorsGenerality?.reasonDelay)}
            type="text"
            variant="outlined"
            value={generality?.reasonDelay || ''}
            onChange={(event) => {
              onChangeGenerality('reasonDelay', event.target.value);
            }}
            inputProps={{
              maxLength: '240',
            }}
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

FormGeneralityBornComponent.displayName = 'Form generality BORN component';

const FormGeneralityBorn = memo(FormGeneralityBornComponent);

export default FormGeneralityBorn;
